import { Alert, Button, formatDateTime } from '@catering/ui';
import { type JSX, useState } from 'react';

import type { InvitationDelivery } from '../users/api';

export interface ActivationLinkNoticeProps {
  delivery: InvitationDelivery;
  expiresAt: string;
}

/**
 * `internal-users/spec.md` → "Invitar y reenviar sin correo saliente
 * disponible". The link lives only in this component's props: it is never put
 * in global state or storage, and goes away with the modal that renders it.
 */
export function ActivationLinkNotice({
  delivery,
  expiresAt,
}: ActivationLinkNoticeProps): JSX.Element | null {
  const [copied, setCopied] = useState(false);

  if (delivery.status === 'SENT') {
    return null;
  }

  if (!('activationUrl' in delivery) || delivery.activationUrl === undefined) {
    return (
      <Alert tone="warning" title="No pudimos mostrarte el enlace de activación">
        {'reason' in delivery && delivery.reason === 'LINK_WITHHELD' ? (
          <p>
            La invitación quedó creada, pero no podemos mostrarte el enlace porque la persona
            invitada tendrá permisos que vos no tenés. Pedile a un SUPER_ADMIN que reenvíe la
            invitación para obtener el enlace.
          </p>
        ) : (
          <p>
            La invitación quedó creada, pero no se envió ningún correo ni hay un enlace para
            compartir. Reenviá la invitación para generar uno nuevo.
          </p>
        )}
      </Alert>
    );
  }

  const { activationUrl } = delivery;

  async function copy(): Promise<void> {
    await navigator.clipboard.writeText(activationUrl);
    setCopied(true);
  }

  return (
    <Alert tone="warning" title="No se envió el correo: compartí el enlace de activación">
      <p>
        No se envió ningún correo porque el correo saliente no está disponible. Copiá este enlace y
        entregáselo a la persona invitada por otro medio. Solo se muestra ahora: si cerrás esta
        ventana no podrás verlo de nuevo y tendrás que reenviar la invitación.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input
          readOnly
          aria-label="Enlace de activación"
          value={activationUrl}
          onFocus={(event) => event.target.select()}
          className="h-11 min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
        />
        <Button type="button" variant="secondary" onClick={() => void copy()}>
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <p className="mt-2 text-xs">Vence el {formatDateTime(expiresAt)}.</p>
    </Alert>
  );
}
